import React, { useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { Link, useParams, useLocation } from "react-router-dom";
import contactIcon from "~/assets/images/contact-icon.svg";
import customerIcon from "~/assets/images/contact-customer.svg";
import schedulerIcon from "~/assets/images/contact-scheduler.svg";
import phoneIcon from "~/assets/images/contact-phone-icon.svg";
import chatIcon from "~/assets/images/contact-chat-icon.svg";
import videoIcon from "~/assets/images/contact-video-icon.svg";
import { createOrderMeeting } from "~/helpers/backendHelper";
import Message from "~/model/message";
import { route, routes } from "~/helpers/routeHelper";
import { formatPhone, openInNewWindow, showError } from "~/helpers/utilHelper";
import { useAppSelector } from "~/hooks/redux";
import { matchPath } from "react-router";

type TSelectedPerson = {
  orderSignerFullName?: string;
  schedulerFullName?: string;
  dealerFullName?: string;
  phone?: string;
};

const ContactDesktop = () => {
  // get order id from url
  const { id: idParam } = useParams<{ id?: string }>();
  const id = idParam ? parseInt(idParam) : 0;

  const location = useLocation();

  const showRoute = !!matchPath({ path: routes.view_notary_bid }, location.pathname);

  const order = useAppSelector(state => state.Order.Single.order);

  const notaryBid = useAppSelector(state => state.NotaryBid.Single.notaryBid);

  const [contactModal, setContactModal] = useState<boolean>(false);

  const [detailedContactModal, setDetailedContactModal] = useState<boolean>(false);

  const [selectedPerson, setSelectedPerson] = useState<TSelectedPerson | undefined>(undefined);

  const contactPersonName = () => {
    if (selectedPerson?.schedulerFullName) {
      return "scheduler";
    }
    if (selectedPerson?.dealerFullName) {
      return "dealer";
    }
    return "customer";
  }

  const contactPersonChannel = () => {
    if (selectedPerson?.schedulerFullName) {
      return Message.CHANNEL_SCHEDULER_NOTARY;
    }

    if (selectedPerson?.dealerFullName) {
      return Message.CHANNEL_DEALER_SCHEDULER_NOTARY;
    }

    return Message.CHANNEL_NOTARY_CUSTOMER;
  }

  const toggle = () => setContactModal(!contactModal);

  const toggleDetailed = () => {
    setDetailedContactModal(current => !current);
  };

  const contactCustomer = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // prevent Link component from redirecting
    event.preventDefault();
    setSelectedPerson({
      orderSignerFullName: (order as any)?.orderSignerFullName,
      phone: (order as any)?.orderSignerPhone
    });

    toggleDetailed();
  };

  const contactScheduler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // prevent Link component from redirecting
    event.preventDefault();

    setSelectedPerson({
      schedulerFullName: !showRoute ? (order as any)?.schedulerFullName : (notaryBid as any)?.schedulerFullName,
      phone: !showRoute ? (order as any)?.schedulerPhone : (notaryBid as any)?.schedulerPhone
    });

    toggleDetailed();
  };

  const contactDealer = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    setSelectedPerson({
      dealerFullName: !showRoute ? (order as any)?.dealerFullName : (notaryBid as any)?.dealerFullName,
      phone: !showRoute ? (order as any)?.dealerPhone : (notaryBid as any)?.dealerPhone
    });

    toggleDetailed();
  };

  const startVideoCall = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (selectedPerson?.schedulerFullName) {
      createMeeting("scheduler");
    }

    if (selectedPerson?.orderSignerFullName) {
      createMeeting("customer");
    }
  };

  const createMeeting = async (role: string) => {
    try {
      const { link } = await createOrderMeeting(id, role);
      if (!link) {
        throw new Error();
      } else {
        openInNewWindow(link);
      }
    } catch (err: any) {
      showError("Unable to start meeting");
    }
  };

  return (
    <React.Fragment>
      <div className="contact-btn btn header-item" onClick={toggle}>
        <img className="contact-btn-icon" alt="contact" src={contactIcon} />
      </div>
      <Modal
        className="contact-modal"
        isOpen={contactModal}
        toggle={toggle}
        centered
        fade={false}
        backdrop={!detailedContactModal}
      >
        <button type="button" className="btn-close ms-auto p-2" aria-label="Close" onClick={toggle} />
        <div className="contact-modal-header">
          <h4 className="contact-modal-title">Select recipient</h4>
          <p className="contact-modal-subtitle">Choose the recipient from below</p>
        </div>
        <ModalBody>
          <ul className="list-unstyled chat-list" id="recent-list">
            {!showRoute &&
              <li>
                <Link to="" onClick={contactCustomer}>
                  <div className="d-flex">
                    <div className="align-self-center me-3">
                      <img src={customerIcon} alt="chat" />
                    </div>

                    <div className="flex-grow-1 overflow-hidden my-auto">
                      <h5 className="contact-item-title text-truncate">
                        {(order as any)?.orderSignerFullName}
                      </h5>
                      <p className="contact-item-subtitle text-truncate">
                        Customer
                      </p>
                    </div>
                  </div>
                </Link>
              </li>
            }
            <li>
              <Link to="" onClick={contactScheduler}>
                <div className="d-flex">
                  <div className="align-self-center me-3">
                    <img src={schedulerIcon} alt="chat" />
                  </div>

                  <div className="flex-grow-1 overflow-hidden my-auto">
                    <h5 className="contact-item-title text-truncate">
                      {!showRoute ? (order as any)?.schedulerFullName : (notaryBid as any)?.schedulerFullName}
                    </h5>
                    <p className="contact-item-subtitle text-truncate">
                      Scheduler
                    </p>
                  </div>
                </div>
              </Link>
            </li>
            {!!order?.dealerFullName && !order?.orderNotaryIsInactive &&
              <li>
                <Link to="" onClick={contactDealer}>
                  <div className="d-flex">
                    <div className="align-self-center me-3">
                      <img src={customerIcon} alt="chat" />
                    </div>

                    <div className="flex-grow-1 overflow-hidden my-auto">
                      <h5 className="contact-item-title text-truncate">
                        {!showRoute ? (order as any)?.dealerFullName : (notaryBid as any)?.dealerFullName}
                      </h5>
                      <p className="contact-item-subtitle text-truncate">
                        Dealer
                      </p>
                    </div>
                  </div>
                </Link>
              </li>
            }
          </ul>
        </ModalBody>
      </Modal>
      <Modal
        className="contact-modal"
        isOpen={detailedContactModal}
        toggle={toggleDetailed}
        centered
        fade={false}
        onOpened={() => setContactModal(false)}
      >
        <button type="button" className="btn-close ms-auto p-2" aria-label="Close" onClick={toggleDetailed} />
        <div className="contact-modal-header">
          <h4 className="contact-modal-title">Contact options</h4>
          <p className="contact-modal-subtitle">Choose one of the contact options</p>
        </div>
        <ModalBody>
          <ul className="list-unstyled chat-list" id="recent-list">
            <li>
              <Link to="" className="pe-none">
                <div className="d-flex">
                  <div className="align-self-center me-3">
                    <img src={phoneIcon} alt="chat" />
                  </div>

                  <div className="flex-grow-1 overflow-hidden my-auto">
                    <h5 className="contact-item-title text-truncate">
                      Phone call
                    </h5>
                    <p className="contact-item-subtitle text-truncate">
                      {formatPhone(selectedPerson?.phone) || "--"}
                    </p>
                  </div>
                </div>
              </Link>
            </li>
            {!showRoute &&
              <li>
                <Link
                  to={route(routes.view_order_messages, [!showRoute ? id : (notaryBid as any)?.orderId, contactPersonChannel()])}
                  onClick={toggleDetailed}
                >
                  <div className="d-flex">
                    <div className="align-self-center me-3">
                      <img src={chatIcon} alt="chat" />
                    </div>

                    <div className="flex-grow-1 overflow-hidden my-auto">
                      <h5 className="contact-item-title text-truncate">
                        Chat
                      </h5>
                      <p className="contact-item-subtitle text-truncate">
                        Contact your {contactPersonName()}
                      </p>
                    </div>
                  </div>
                </Link>
              </li>
            }
            {!showRoute && !selectedPerson?.dealerFullName && !showRoute &&
              <li>
                <Link to="" onClick={startVideoCall}>
                  <div className="d-flex">
                    <div className="align-self-center me-3">
                      <img src={videoIcon} alt="chat" />
                    </div>

                    <div className="flex-grow-1 overflow-hidden my-auto">
                      <h5 className="contact-item-title text-truncate">
                        Video call
                      </h5>
                    </div>
                  </div>
                </Link>
              </li>
            }
          </ul>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ContactDesktop;